import * as React from "react";
import { RefreshCw } from "lucide-react";
import { strings } from "@src/i18n/strings";
import { colors } from "@taskpane/theme";

export type AppView = "email" | "dashboard";

const appStrings = strings.components.app;

const barStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  padding: "4px 12px",
  background: colors.surface,
  borderBottom: `1px solid ${colors.border}`,
};
const tabListStyle: React.CSSProperties = {
  display: "inline-flex",
  padding: "2px",
  borderRadius: "6px",
  background: colors.surfaceAlt,
};
const tabStyle = (active: boolean): React.CSSProperties => ({
  minHeight: "24px",
  padding: "0 10px",
  border: "none",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: 600,
  cursor: active ? "default" : "pointer",
  background: active ? colors.surface : "transparent",
  color: active ? colors.primary : colors.textMuted,
  boxShadow: active ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
});
const refreshButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  padding: 0,
  border: "none",
  borderRadius: "4px",
  background: "transparent",
  color: colors.textMuted,
  cursor: "pointer",
};
const TABS: Array<{ id: AppView; label: string }> = [
  { id: "email", label: appStrings.tabs.email },
  { id: "dashboard", label: appStrings.tabs.dashboard },
];

export interface AppTopBarProps {
  view: AppView;
  onViewChange: (view: AppView) => void;
  onRefresh: () => void;
  refreshing: boolean;
}

export default function AppTopBar({ view, onViewChange, onRefresh, refreshing }: AppTopBarProps) {
  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const next: AppView = view === "email" ? "dashboard" : "email";

    onViewChange(next);
    const target = e.currentTarget.parentElement?.querySelector<HTMLButtonElement>(
      `[data-view="${next}"]`
    );

    target?.focus();
  };

  return (
    <div className="app-top-bar" style={barStyle}>
      <div role="tablist" aria-label={appStrings.aria.viewTabs} style={tabListStyle}>
        {TABS.map((tab) => {
          const active = tab.id === view;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              data-view={tab.id}
              className="app-top-bar-tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              style={tabStyle(active)}
              onClick={() => onViewChange(tab.id)}
              onKeyDown={handleTabKeyDown}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="app-top-bar-refresh"
        style={refreshButtonStyle}
        onClick={onRefresh}
        disabled={refreshing}
        aria-busy={refreshing}
        aria-label={refreshing ? appStrings.refreshing : appStrings.refresh}
        title={appStrings.refresh}
      >
        <RefreshCw
          size={16}
          aria-hidden="true"
          className={refreshing ? "app-top-bar-spin" : undefined}
        />
      </button>
    </div>
  );
}
